import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ContactMail, Science, MarkEmailUnread, Inbox } from '@mui/icons-material';
import { getContacts, getRfqs } from '../../services/api';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const CARD_SHADOW_HOVER = '0 8px 30px rgba(108, 152, 173, 0.25)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

const statThemes = [
  { bg: 'linear-gradient(135deg, #6C98AD 0%, #8eb4c7 100%)', icon: <ContactMail fontSize="large" /> },
  { bg: 'linear-gradient(135deg, #e8833a 0%, #f5a623 100%)', icon: <MarkEmailUnread fontSize="large" /> },
  { bg: 'linear-gradient(135deg, #24272a 0%, #3d4348 100%)', icon: <Science fontSize="large" /> },
  { bg: 'linear-gradient(135deg, #7b61c1 0%, #9b87d6 100%)', icon: <Inbox fontSize="large" /> },
];

const StatCard = ({ title, value, theme: t }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: RADIUS,
      border: '2px solid rgba(108, 152, 173, 0.12)',
      boxShadow: CARD_SHADOW,
      transition: TRANSITION,
      overflow: 'visible',
      '&:hover': {
        boxShadow: CARD_SHADOW_HOVER,
        borderColor: PRIMARY,
        transform: 'translateY(-4px)',
      },
    }}
  >
    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant="body2"
            sx={{ color: '#888', fontFamily: FONT_BODY, fontSize: '0.85rem', mb: 0.5 }}
          >
            {title}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: FONT_HEADING,
              fontWeight: 700,
              color: SECONDARY,
              fontSize: '2.2rem',
            }}
          >
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: RADIUS,
            background: t.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            boxShadow: '0 4px 12px rgba(108, 152, 173, 0.3)',
            transition: TRANSITION,
          }}
        >
          {t.icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchStats() {
      try {
        const [contactsRes, demosRes] = await Promise.all([
          getContacts({ limit: 1 }),
          getRfqs({ limit: 1 }),
        ]);

        const [unreadContacts, unreadDemos] = await Promise.all([
          getContacts({ limit: 1, read: false }),
          getRfqs({ limit: 1, status: 'UNREAD' }),
        ]);

        setStats({
          totalContacts: contactsRes.total || 0,
          unreadContacts: unreadContacts.total || 0,
          totalRfqs: demosRes.total || 0,
          unreadRfqs: unreadDemos.total || 0,
        });
      } catch (err) {
        setError(err.message || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2, borderRadius: RADIUS }}>{error}</Alert>;
  }

  const cards = [
    { title: 'Total Contacts', value: stats.totalContacts },
    { title: 'Unread Contacts', value: stats.unreadContacts },
    { title: 'Total Requests for Quote (RFQ)', value: stats.totalRfqs },
    { title: 'Unread Requests for Quote (RFQ)', value: stats.unreadRfqs },
  ];

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontFamily: FONT_HEADING,
          fontWeight: 700,
          background: `linear-gradient(135deg, ${SECONDARY} 0%, ${PRIMARY} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          mb: 0.5,
        }}
      >
        Dashboard
      </Typography>
      <Typography variant="body1" sx={{ color: '#888', fontFamily: FONT_BODY, mb: 4 }}>
        Overview of your admin panel activity.
      </Typography>

      <Grid container spacing={3}>
        {cards.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <StatCard title={card.title} value={card.value} theme={statThemes[i]} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
